import { UserInformation } from "@/api/signin";

export const VERIFY_ACCOUNT_STEPS = [
	"pin",
	"question",
	"verify_phone",
	"verify_phone_code",
	"verify_email",
	"business_details",
] as const;

export type VerifyAccountStep = (typeof VERIFY_ACCOUNT_STEPS)[number];

const VERIFY_ACCOUNT_INITIAL_STEP_KEY = "verifyAccountInitialStep";

/** Same bar as linked-accounts/new: need at least one business with a name. */
export function userNeedsSignUpBusinessDetails(user: UserInformation): boolean {
	const primary = user.guest_business_details[0];
	const companyName = primary?.business_name?.trim() ?? "";
	return user.guest_business_details.length === 0 || !companyName;
}

export function getNextVerifyStepAfterBusinessDetails(
	user: UserInformation,
): VerifyAccountStep {
	if (user.location_registration?.missing_steps.includes("PIN")) {
		return "pin";
	}
	return "question";
}

export function getVerifyAccountInitialStep(
	user: UserInformation,
): VerifyAccountStep | null {
	if (!user.has_verified_email) {
		return "verify_email";
	}

	if (!user.has_verified_phone_number) {
		return "verify_phone";
	}

	if (userNeedsSignUpBusinessDetails(user)) {
		return "business_details";
	}

	if (user.location_registration?.missing_steps.includes("PIN")) {
		return "pin";
	}

	return null;
}

/**
 * Whether the guest may use `/main` without being sent to verify-account.
 * Guests who already verified their phone are not redirected solely for missing
 * business details (legacy accounts); the in-flow wizard can still show that step.
 */
export function verifyAccountSetupAllowsMainAppAccess(
	user: UserInformation,
): boolean {
	const step = getVerifyAccountInitialStep(user);
	if (step === null) {
		return true;
	}
	if (step === "business_details" && user.has_verified_phone_number) {
		return true;
	}
	return false;
}

export function setVerifyAccountInitialStep(step: VerifyAccountStep): void {
	sessionStorage.setItem(VERIFY_ACCOUNT_INITIAL_STEP_KEY, step);
}
